import React, { useState } from 'react';
import { Clock, MapPin, CheckCircle, Circle, Play, Radio, Edit2, Save, X, Info } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentScheduleAPI } from '../services/tournamentApi';
import { apiCall } from '../../services/apiUtil';
import { isEnabled, TOURNAMENT_ENTITIES, getDisabledReason } from '../access';
import toast from 'react-hot-toast';

const STATUS_BADGES = {
  scheduled: { label: 'Scheduled', className: 'bg-gray-500 text-white' },
  checking_in: { label: 'Checking In', className: 'bg-yellow-500 text-black' },
  ready: { label: 'Ready', className: 'bg-green-500 text-white' },
  live: { label: 'Live', className: 'bg-red-500 text-white animate-pulse' },
  completed: { label: 'Completed', className: 'bg-blue-500 text-white' },
  bye: { label: 'BYE', className: 'bg-gray-400 text-white' },
};

export default function GameCard({
  game,
  tournament,
  tournamentVO,
  participants = [],
  userTeamIds = [],
  isScorer = false,
  onCheckInSuccess,
  onStartGame,
  onOpenLive,
  onStartScoring,
  onParticipantUpdate,
  externalEditMode = false,
  onExitEditMode,
}) {

  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editingParticipants, setEditingParticipants] = useState(false);
  const [selectedParticipantA, setSelectedParticipantA] = useState(null);
  const [selectedParticipantB, setSelectedParticipantB] = useState(null);

  // Per-match player subset — only relevant when the sport is played
  // individually (singles/doubles) and a selected team's roster is larger
  // than what that format actually puts on court.
  const [selectedPlayersA, setSelectedPlayersA] = useState([]);
  const [selectedPlayersB, setSelectedPlayersB] = useState([]);
  const [teamRosterA, setTeamRosterA] = useState(null);
  const [teamRosterB, setTeamRosterB] = useState(null);
  const [loadingRosterA, setLoadingRosterA] = useState(false);
  const [loadingRosterB, setLoadingRosterB] = useState(false);

  const sportPlayedBy = tournament?.sport_typea?.played_by || tournament?.sportType?.playedBy || null;
  const tournamentRequiredPlayerCount = tournament?.participantsType === 'doubles' ? 2
    : tournament?.participantsType === 'singles' ? 1
    : null;
  // A specific game can play by different rules than the tournament overall
  // — an MLP Dreambreaker is always singles even in an otherwise-doubles
  // tournament, and any match with an explicit settings override (see the
  // schedule's "Match Settings" gear icon) can pick its own participantsType
  // too. Either one takes priority over the tournament-wide default, so the
  // player picker asks for the right number of players for *this* match.
  const gameParticipantsType = game?.overridden_match_settings?.participantsType
    || (game?.game_type === 'dreambreaker' ? 'singles' : null);
  const requiredPlayerCount = gameParticipantsType === 'doubles' ? 2
    : gameParticipantsType === 'singles' ? 1
    : tournamentRequiredPlayerCount;
  const needsPlayerPicker = sportPlayedBy?.toLowerCase() === 'individual' && requiredPlayerCount != null;

  // Only actually show/require the picker once we know the roster genuinely
  // exceeds what the format needs — a team with exactly 2 members in a
  // doubles tournament has nothing to pick, same as before this feature.
  const pickerActiveA = needsPlayerPicker && selectedParticipantA?.type === 'team'
    && Array.isArray(teamRosterA) && teamRosterA.length > requiredPlayerCount;
  const pickerActiveB = needsPlayerPicker && selectedParticipantB?.type === 'team'
    && Array.isArray(teamRosterB) && teamRosterB.length > requiredPlayerCount;

  const fetchTeamRoster = async (teamId, setRoster, setLoadingRoster) => {
    setLoadingRoster(true);
    try {
      const teamData = await apiCall(`/teams/${teamId}`);
      const players = (teamData.members || []).map(m => ({
        id: m.user.id,
        name: `${m.user.firstName} ${m.user.lastName}`.trim(),
      }));
      setRoster(players);
    } catch (error) {
      console.error('[GameCard] Failed to fetch team roster:', error);
      setRoster([]);
    } finally {
      setLoadingRoster(false);
    }
  };

  // Fetch each side's roster whenever the selected team changes (and this
  // sport/tournament combination actually needs a player subset) — this is
  // the only way to know whether the roster even exceeds requiredPlayerCount.
  React.useEffect(() => {
    if (needsPlayerPicker && selectedParticipantA?.type === 'team') {
      fetchTeamRoster(selectedParticipantA.id, setTeamRosterA, setLoadingRosterA);
    } else {
      setTeamRosterA(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParticipantA?.type, selectedParticipantA?.id, needsPlayerPicker]);

  React.useEffect(() => {
    if (needsPlayerPicker && selectedParticipantB?.type === 'team') {
      fetchTeamRoster(selectedParticipantB.id, setTeamRosterB, setLoadingRosterB);
    } else {
      setTeamRosterB(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParticipantB?.type, selectedParticipantB?.id, needsPlayerPicker]);

  const togglePlayerSelection = (player, selected, setSelected) => {
    const isSelected = selected.some(p => p.id === player.id);
    if (isSelected) {
      setSelected(selected.filter(p => p.id !== player.id));
    } else if (selected.length < requiredPlayerCount) {
      setSelected([...selected, player]);
    }
  };

  // Sync external edit mode with internal state
  React.useEffect(() => {
    if (externalEditMode && !editingParticipants) {
      // Pre-fill with current participants
      const { participant_a_id, participant_a_type, participant_b_id, participant_b_type } = game;
      const participantOptions = participants
        .filter(p => p.teamId || (p.participantId && p.player))
        .map(p => ({
          id: p.teamId || p.participantId,
          type: p.teamId ? 'team' : 'user',
          name: p.teamName || p.team?.teamName || p.participantName || `Player ${p.participantId}`,
          logo: p.team?.teamLogo,
        }));

      if (participant_a_id) {
        // participant_a_id comes from the DB as a string (VarChar column);
        // participantOptions' id is numeric (teamId/participantId) — compare
        // as strings so an already-assigned team actually pre-fills instead
        // of silently falling through to null.
        const currentA = participantOptions.find(
          p => p.type === participant_a_type && String(p.id) === String(participant_a_id)
        );
        setSelectedParticipantA(currentA || null);
      }
      if (participant_b_id) {
        const currentB = participantOptions.find(
          p => p.type === participant_b_type && String(p.id) === String(participant_b_id)
        );
        setSelectedParticipantB(currentB || null);
      }
      // Restore any previously-saved player subset so re-opening the editor
      // doesn't lose an already-made selection.
      setSelectedPlayersA(game.participant_a_players || []);
      setSelectedPlayersB(game.participant_b_players || []);
      setEditingParticipants(true);
    }
  }, [externalEditMode, editingParticipants, game, participants]);

  const {
    id,
    round_name,
    match_number,
    participant_a_type,
    participant_a_id,
    participant_a_name,
    participant_a_players,
    participant_b_type,
    participant_b_id,
    participant_b_name,
    participant_b_players,
    checkin_status,
    checkins = [],
    event,
    status,
    score_summary,
    winner_id,
    winner_type,
  } = game;

  // Parse score_summary ("11-0, 2-11, 11-0") to derive games won for each side
  const parseScoreSummary = (summary) => {
    if (!summary) return null;
    const games = summary.split(',').map(s => {
      const parts = s.trim().split('-');
      const a = parseInt(parts[0]);
      const b = parseInt(parts[1]);
      return { a: isNaN(a) ? 0 : a, b: isNaN(b) ? 0 : b };
    }).filter(g => !isNaN(g.a) && !isNaN(g.b));
    if (games.length === 0) return null;
    return {
      winsA: games.filter(g => g.a > g.b).length,
      winsB: games.filter(g => g.b > g.a).length,
      games,
    };
  };
  const parsedScore = parseScoreSummary(score_summary);

  // Determine if current user can check in for a participant
  // Only scorers (or organizers) can perform check-ins
  const canCheckInForA = () => {
    if (!isScorer) return false; // Only scorers can check in
    if (tournament?.status === 'completed') return false; // Tournament already ended — same gate as Start Scoring
    if (checkin_status === 'live' || checkin_status === 'completed') return false;
    if (!participant_a_id) return false; // No participant assigned yet

    const isCheckedIn = checkins.some(
      (c) => c.participant_type === participant_a_type && c.participant_id === participant_a_id
    );
    return !isCheckedIn;
  };

  const canCheckInForB = () => {
    if (!isScorer) return false; // Only scorers can check in
    if (tournament?.status === 'completed') return false; // Tournament already ended — same gate as Start Scoring
    if (checkin_status === 'live' || checkin_status === 'completed') return false;
    if (!participant_b_id) return false; // No participant assigned yet

    const isCheckedIn = checkins.some(
      (c) => c.participant_type === participant_b_type && c.participant_id === participant_b_id
    );
    return !isCheckedIn;
  };

  const isParticipantACheckedIn = checkins.some(
    (c) => c.participant_type === participant_a_type && c.participant_id === participant_a_id
  );

  const isParticipantBCheckedIn = checkins.some(
    (c) => c.participant_type === participant_b_type && c.participant_id === participant_b_id
  );

  const handleCheckIn = async (participantType, participantId) => {
    setLoading(true);
    try {
      const result = await tournamentScheduleAPI.checkIn(id, {
        participant_type: participantType,
        participant_id: participantId,
      });

      // Show single success message
      toast.success('Checked in successfully!');
      onCheckInSuccess?.(game.id, result);
    } catch (error) {
      console.error('[GameCard] Check-in error:', error);
      toast.error(error.message || 'Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async () => {
    setLoading(true);
    try {
      const result = await tournamentScheduleAPI.startGame(id);
      // Toast message shown in parent component (ScheduleTab)
      onStartGame?.(game.id, result);
    } catch (error) {
      toast.error(error.message || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateParticipants = async () => {
    if (!selectedParticipantA || !selectedParticipantB) {
      toast.error('Please select both participants');
      return;
    }
    if (selectedParticipantA.type === selectedParticipantB.type && selectedParticipantA.id === selectedParticipantB.id) {
      toast.error('Participant A and B cannot be the same team');
      return;
    }
    if (pickerActiveA && selectedPlayersA.length !== requiredPlayerCount) {
      toast.error(`Select exactly ${requiredPlayerCount} player${requiredPlayerCount === 1 ? '' : 's'} for ${selectedParticipantA.name}`);
      return;
    }
    if (pickerActiveB && selectedPlayersB.length !== requiredPlayerCount) {
      toast.error(`Select exactly ${requiredPlayerCount} player${requiredPlayerCount === 1 ? '' : 's'} for ${selectedParticipantB.name}`);
      return;
    }

    setLoading(true);
    try {
      await tournamentScheduleAPI.updateGame(id, {
        participant_a_type: selectedParticipantA.type,
        participant_a_id: selectedParticipantA.id,
        participant_b_type: selectedParticipantB.type,
        participant_b_id: selectedParticipantB.id,
        participant_a_players: pickerActiveA ? selectedPlayersA : null,
        participant_b_players: pickerActiveB ? selectedPlayersB : null,
      });
      toast.success('Participants updated successfully');
      setEditingParticipants(false);
      onExitEditMode?.();
      onParticipantUpdate?.();
    } catch (error) {
      toast.error(error.message || 'Failed to update participants');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingParticipants(false);
    setSelectedParticipantA(null);
    setSelectedParticipantB(null);
    setSelectedPlayersA([]);
    setSelectedPlayersB([]);
    onExitEditMode?.();
  };

  // Prepare participant options from participants list
  const participantOptions = participants
    .filter(p => p.teamId || (p.participantId && p.player))
    .map(p => ({
      id: p.teamId || p.participantId,
      type: p.teamId ? 'team' : 'user',
      name: p.teamName || p.team?.teamName || p.participantName || `Player ${p.participantId}`,
      logo: p.team?.teamLogo,
    }));

  const statusBadge = STATUS_BADGES[checkin_status] || STATUS_BADGES.scheduled;

  // Handle BYE games
  if (status === 'bye') {
    return (
      <div className={`${theme.card} rounded-lg p-4 border ${theme.border} opacity-60`}>
        <div className="flex items-center justify-between">
          <span className={`${theme.textMuted} text-sm`}>Match {match_number}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_BADGES.bye.className}`}>
            BYE
          </span>
        </div>
        <div className={`text-center ${theme.text} mt-2`}>
          {participant_a_name || participant_b_name || 'TBD'} advances
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${theme.card} rounded-lg p-4 border ${theme.border} ${
        checkin_status === 'live' ? 'ring-2 ring-red-500' : ''
      } ${checkin_status === 'completed' ? 'opacity-75' : ''}`}
    >
      {/* Header: Match number */}
      <div className="flex items-center justify-between mb-3">
        <span className={`${theme.textMuted} text-sm`}>Match {match_number}</span>
      </div>

      {/* Info message for non-scorers */}
      {!isScorer && (checkin_status === 'draft' || checkin_status === 'scheduled' || checkin_status === 'checking_in') && (
        <div className={`mb-3 px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border} flex items-start gap-2`}>
          <Info className={`w-4 h-4 ${theme.link} flex-shrink-0 mt-0.5`} />
          <p className={`text-xs ${theme.textSecondary}`}>
            Only tournament scorers can check in teams and start matches.
          </p>
        </div>
      )}

      {/* Participants */}
      <div className="flex items-center justify-between gap-4">
        {/* Participant A */}
        <div className="flex-1 text-center">
          {editingParticipants ? (
            <>
              <select
                value={selectedParticipantA ? `${selectedParticipantA.type}:${selectedParticipantA.id}` : ''}
                onChange={(e) => {
                  const [type, id] = e.target.value.split(':');
                  const participant = participantOptions.find(p => p.type === type && p.id === parseInt(id));
                  setSelectedParticipantA(participant);
                  setSelectedPlayersA([]); // different team — old player picks no longer apply
                }}
                className={`w-full px-2 py-1 text-sm rounded-lg border ${theme.border} ${theme.card} ${theme.text}`}
              >
                <option value="">Select {tournament?.participantsType || 'Team'}</option>
                {participantOptions
                  .filter(p => !(selectedParticipantB && p.type === selectedParticipantB.type && p.id === selectedParticipantB.id))
                  .map(p => (
                    <option key={`${p.type}:${p.id}`} value={`${p.type}:${p.id}`}>
                      {p.name}
                    </option>
                  ))}
              </select>
              {loadingRosterA && (
                <p className={`text-xs ${theme.textMuted} mt-1`}>Loading roster...</p>
              )}
              {pickerActiveA && (
                <div className={`mt-2 p-2 rounded-lg border ${theme.border} text-left`}>
                  <p className={`text-xs ${theme.textMuted} mb-1`}>
                    Playing ({selectedPlayersA.length}/{requiredPlayerCount})
                  </p>
                  {teamRosterA.map(player => {
                    const isChecked = selectedPlayersA.some(p => p.id === player.id);
                    const disableUnchecked = !isChecked && selectedPlayersA.length >= requiredPlayerCount;
                    return (
                      <label key={player.id} className={`flex items-center gap-2 text-xs py-0.5 ${disableUnchecked ? 'opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={disableUnchecked}
                          onChange={() => togglePlayerSelection(player, selectedPlayersA, setSelectedPlayersA)}
                          className="w-3.5 h-3.5"
                        />
                        <span className={theme.text}>{player.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className={`${theme.text} font-medium ${participant_a_players?.length > 0 ? 'mb-0.5' : 'mb-2'}`}>
                {participant_a_name || 'TBD'}
              </div>
              {participant_a_players?.length > 0 && (
                <div className={`text-xs ${theme.textMuted} mb-2`}>
                  {participant_a_players.map(p => p.name.split(' ')[0]).join(' / ')}
                </div>
              )}
              {participant_a_id && (
                <div>
                  {(status === 'completed' || checkin_status === 'completed') && parsedScore ? (
                    <span className="inline-flex items-center text-lg font-bold text-blue-500">
                      {parsedScore.winsA}
                    </span>
                  ) : isParticipantACheckedIn ? (
                    <span className="inline-flex items-center text-green-500 text-xs">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Checked In
                    </span>
                  ) : canCheckInForA() ? (
                    <button
                      onClick={() => handleCheckIn(participant_a_type, participant_a_id)}
                      disabled={loading}
                      className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                    >
                      {loading ? 'Checking in...' : 'Check In'}
                    </button>
                  ) : (
                    <span className="inline-flex items-center text-gray-400 text-xs">
                      <Circle className="w-4 h-4 mr-1" />
                      Waiting
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* VS / FT Divider with individual game scores */}
        <div className="flex flex-col items-center gap-1">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${theme.backgroundSecondary} ${theme.textMuted}`}>
            {checkin_status === 'completed' ? 'FT' : 'VS'}
          </div>
          {parsedScore && parsedScore.games.length > 0 && (
            <div className={`text-xs ${theme.textMuted} text-center leading-tight`}>
              {parsedScore.games.map((g, i) => (
                <div key={i}>{g.a}–{g.b}</div>
              ))}
            </div>
          )}
        </div>

        {/* Participant B */}
        <div className="flex-1 text-center">
          {editingParticipants ? (
            <>
              <select
                value={selectedParticipantB ? `${selectedParticipantB.type}:${selectedParticipantB.id}` : ''}
                onChange={(e) => {
                  const [type, id] = e.target.value.split(':');
                  const participant = participantOptions.find(p => p.type === type && p.id === parseInt(id));
                  setSelectedParticipantB(participant);
                  setSelectedPlayersB([]); // different team — old player picks no longer apply
                }}
                className={`w-full px-2 py-1 text-sm rounded-lg border ${theme.border} ${theme.card} ${theme.text}`}
              >
                <option value="">Select {tournament?.participantsType || 'Team'}</option>
                {participantOptions
                  .filter(p => !(selectedParticipantA && p.type === selectedParticipantA.type && p.id === selectedParticipantA.id))
                  .map(p => (
                    <option key={`${p.type}:${p.id}`} value={`${p.type}:${p.id}`}>
                      {p.name}
                    </option>
                  ))}
              </select>
              {loadingRosterB && (
                <p className={`text-xs ${theme.textMuted} mt-1`}>Loading roster...</p>
              )}
              {pickerActiveB && (
                <div className={`mt-2 p-2 rounded-lg border ${theme.border} text-left`}>
                  <p className={`text-xs ${theme.textMuted} mb-1`}>
                    Playing ({selectedPlayersB.length}/{requiredPlayerCount})
                  </p>
                  {teamRosterB.map(player => {
                    const isChecked = selectedPlayersB.some(p => p.id === player.id);
                    const disableUnchecked = !isChecked && selectedPlayersB.length >= requiredPlayerCount;
                    return (
                      <label key={player.id} className={`flex items-center gap-2 text-xs py-0.5 ${disableUnchecked ? 'opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={disableUnchecked}
                          onChange={() => togglePlayerSelection(player, selectedPlayersB, setSelectedPlayersB)}
                          className="w-3.5 h-3.5"
                        />
                        <span className={theme.text}>{player.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className={`${theme.text} font-medium ${participant_b_players?.length > 0 ? 'mb-0.5' : 'mb-2'}`}>
                {participant_b_name || 'TBD'}
              </div>
              {participant_b_players?.length > 0 && (
                <div className={`text-xs ${theme.textMuted} mb-2`}>
                  {participant_b_players.map(p => p.name.split(' ')[0]).join(' / ')}
                </div>
              )}
              {participant_b_id && (
                <div>
                  {(status === 'completed' || checkin_status === 'completed') && parsedScore ? (
                    <span className="inline-flex items-center text-lg font-bold text-blue-500">
                      {parsedScore.winsB}
                    </span>
                  ) : isParticipantBCheckedIn ? (
                    <span className="inline-flex items-center text-green-500 text-xs">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Checked In
                    </span>
                  ) : canCheckInForB() ? (
                    <button
                      onClick={() => handleCheckIn(participant_b_type, participant_b_id)}
                      disabled={loading}
                      className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                    >
                      {loading ? 'Checking in...' : 'Check In'}
                    </button>
                  ) : (
                    <span className="inline-flex items-center text-gray-400 text-xs">
                      <Circle className="w-4 h-4 mr-1" />
                      Waiting
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Save/Cancel buttons for editing mode */}
      {editingParticipants && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <button
            onClick={handleCancelEdit}
            disabled={loading}
            className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-1`}
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
          <button
            onClick={handleUpdateParticipants}
            disabled={
              loading || !selectedParticipantA || !selectedParticipantB
              || (pickerActiveA && selectedPlayersA.length !== requiredPlayerCount)
              || (pickerActiveB && selectedPlayersB.length !== requiredPlayerCount)
            }
            className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-1`}
          >
            <Save className="w-3.5 h-3.5" />
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {/* Event Details */}
      {event && (event.start_time || event.venue?.fullAddress) && (
        <div className={`mt-3 pt-3 border-t ${theme.border}`}>
          <div className={`flex flex-col gap-2 text-xs ${theme.textMuted}`}>
            {/* Date & Time */}
            {event.start_time && (
              <div className="flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 mr-1.5" />
                <span>
                  {new Date(event.start_time).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {' at '}
                  {new Date(event.start_time).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                  {event.end_time && (
                    <span>
                      {' - '}
                      {new Date(event.end_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                  )}
                </span>
              </div>
            )}
            {/* Location */}
            {event.venue?.fullAddress && (
              <div className="flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                <span>{event.venue.name || event.venue.fullAddress}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-3 mt-4">
        {/* Start Scoring button - replaces Start Match; scorer only, disabled until both participants check in */}
        {(() => {
          const canStartGame = tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.GAME_START, tournamentVO, { game });
          const disabledReason = tournamentVO && !canStartGame ? getDisabledReason(TOURNAMENT_ENTITIES.GAME_START, tournamentVO, { game }) : '';

          return isScorer && checkin_status !== 'live' && checkin_status !== 'completed' && status !== 'completed' && status !== 'Completed' && (
            <button
              onClick={() => canStartGame && onStartScoring?.(game)}
              disabled={loading || !canStartGame}
              className={`${btn.base} ${btn.md} flex items-center ${
                canStartGame ? theme.buttonPrimary : `${theme.buttonGhost} cursor-not-allowed`
              }`}
              title={canStartGame ? 'Start scoring this match' : disabledReason}
            >
              <Radio className="w-4 h-4 mr-2" />
              Start Scoring
              {!canStartGame && disabledReason && (
                <span className="ml-2 text-xs opacity-75">({disabledReason})</span>
              )}
            </button>
          );
        })()}

        {/* Live button - show when game is in progress and not completed, but only if event is scheduled */}
        {checkin_status === 'live' && status !== 'Completed' && checkin_status !== 'completed' && (
          event ? (
            <button
              onClick={() => onOpenLive?.(game)}
              className={`${btn.base} ${btn.md} ${theme.buttonDanger} flex items-center`}
            >
              <Radio className="w-4 h-4 mr-2 animate-pulse" />
              Live
            </button>
          ) : (
            <div className="flex items-center px-4 py-2 rounded-lg bg-gray-400 text-white text-sm font-medium cursor-not-allowed opacity-75">
              <Radio className="w-4 h-4 mr-2" />
              <span>Schedule game first to go live</span>
            </div>
          )
        )}

        {/* View button - show when game is completed */}
        {(status === 'Completed' || checkin_status === 'completed') && (
          <button
            onClick={() => onOpenLive?.(game)}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center`}
          >
            <Play className="w-4 h-4 mr-2" />
            View
          </button>
        )}
      </div>
    </div>
  );
}
